import {
    Component, Mixins, Watch, Emit,
} from 'vue-property-decorator';
import isEqual from 'lodash.isequal';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Multiselect from 'vue-multiselect';
import FormsMixin from '@components/shared/mixins/forms/index';
import Classes from '@components/shared/mixins/classes';
import config from '@components/shared/config';
import Props from './props';

/**
 * @name Select
 */
@Component({
    components: {
        Multiselect,
    },
})
class YSelect extends Mixins(Props, Classes, FormsMixin) {
    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['color', 'size', 'icon', 'sharp', 'rounded', 'flat', 'raised', 'pill', 'outline', 'bordered'];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.select);

    parsedOptions: Array<object> = this.getValue('options') || null;
    selected: Array<object|string> | object = this.getBoolean('multiple') ? [] : null;
    selectedSet: Array<object|string> | object = this.getBoolean('multiple') ? [] : null;
    isSelectedSet: boolean = false;
    searched: boolean = false;
    isSearching: boolean = false;
    delayTimer: any = null;

    /**
     * Create Common Props for the select.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the select.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Select', this.commonProps, this.baseClass || config.prefix.select));
        if (this.getBoolean('disabled')) {
            cls.push('is-disabled');
        }
        if (this.getBoolean('readonly')) {
            cls.push('is-readonly');
        }
        return cls;
    }

    /**
     * Placeholder Value
     * 
     * @returns {string}
     */
    get placeholderValue(): string {
        const placeholder: string = this.hasEndpoint ? this.$tc('forms.select.placeholder.search') : this.$tc('forms.select.placeholder.default');
        return this.placeholder || placeholder;
    }

    /**
     * TagPlaceholder Value
     * 
     * @returns {string}
     */
    get tagPlaceholderValue(): string {
        return this.tagPlaceholder || this.$tc('forms.select.placeholder.tag');
    }

    /**
     * Check if the endpoint is provided
     * 
     * @returns {boolean}
     */
    get hasEndpoint(): boolean {
        return !!this.getObject('endpoint', 'name', false);
    }

    /**
     * Check if has search query
     * 
     * @returns {boolean}
     */
    get hasSearch(): boolean {
        return this.getBoolean('search') && this.getValue('search') !== 'search';
    }

    /**
     * Check if any option is selected
     * 
     * @returns {boolean}
     */
    get hasSelected(): boolean {
        return this.getBoolean('multiple') && this.selected && Array.isArray(this.selected) ? !!this.selected.length : !!this.selected;
    }

    /**
     * Return the message for no-option slot
     * 
     * @returns {string}
     */
    get noOptionMessage(): string {
        let msg: string = this.$tc('forms.select.message.no_options');
        if (this.hasEndpoint) {
            msg = this.searched ? this.$tc('forms.select.message.no_options') : this.$tc('forms.select.message.no_search');
        }
        return msg;
    }

    /**
     * Props to pass to the vue-multiselect
     * 
     * @returns {object}
     */
    get props(): object {
        return {
            allowEmpty    : this.getBoolean('allow_empty'),
            disabled      : this.getBoolean('disabled') || this.getBoolean('readonly'),
            multiple      : this.getBoolean('multiple'),
            taggable      : this.getBoolean('taggable'),
            closeOnSelect : this.getBoolean('multiple') ? this.getBoolean('close_on_select') : true,
            internalSearch: !this.hasEndpoint,

            name       : this.getValue('name'),
            label      : this.getValue('label_field'),
            customLabel: this.customLabel,

            deselectGroupLabel: this.$t('forms.select.label.group.deselect'),
            deselectLabel     : this.$t('forms.select.label.single.deselect'),
            selectGroupLabel  : this.$t('forms.select.label.group.select'),
            selectLabel       : this.$t('forms.select.label.single.select'),
            selectedLabel     : this.$t('forms.select.label.selected'),

            groupLabel : this.getValue('group_label'),
            groupSelect: this.getValue('group_select'),
            groupValues: this.getValue('group_values'),

            placeholder   : this.placeholderValue,
            tagPlaceholder: this.tagPlaceholderValue,

            trackBy: this.getValue('label_field'),
        };
    }
    
    /**
     * @inheritdoc
     */
    mounted() {
        if (this.getValue('options') && this.getValue('options').length) {
            this.parsedOptions = this.getValue('options');
        }
        this.handleValueChange((this.init || this.getValue('value')), null);
    }

    /**
     * Set new selected item when value changes
     *
     * @param {*} val
     * @param {*} oldVal
     */
    @Watch('init', { deep: true })
    onValueChange(val: any, oldVal: any) {
        this.handleValueChange(val, oldVal);
    }

    /**
     * Set new selected item when value changes
     *
     * @param {*} val
     * @param {*} oldVal
     * @returns {*}
     */
    @Watch('selected', { deep: true })
    @Emit('input')
    onSelectedChange(val: any, oldVal: any): any {
        if (!isEqual(val, oldVal)) {
            return val;
        }
        return null;
    }

    /**
     * Set new selected item when value changes
     *
     * @param {*} val
     * @param {*} oldVal
     */
    @Watch('options', { immediate: true, deep: true })
    onOptionsChange(val: any, oldVal: any) {
        if (!isEqual(val, oldVal)) {
            this.parsedOptions = val;
            this.handleValueChange(this.init, null);
        }
    }

    /**
     * Handle value and options changes
     *
     * @param {*} newVal
     * @param {*} oldVal
     */
    handleValueChange(newVal: any, oldVal: any) {
        if (this.getBoolean('multiple') && newVal) {
            const val = !Array.isArray(newVal) ? [newVal] : newVal;
            if (!isEqual(val, oldVal)) {
                this.selected = val;
                this.selectedSet = this.parsedOptions.filter((item) => val.includes(item[this.getValue('value_field')]));
            }
        } else if (newVal !== oldVal) {
            this.selected = newVal;
            if (typeof newVal === 'object') {
                this.selectedSet = newVal;
            } else {
                this.selectedSet = this.parsedOptions.find((item) => item[this.getValue('value_field')] === newVal);
            }
        }
    }

    /**
     * Set selected item
     *
     * @param {object|Array<object>} options
     */
    setSelected(options: object | Array<object>) {
        this.selectedSet = options;
        if (Array.isArray(options)) {
            this.selected = options.map((item) => item[this.getValue('value_field')]);
            return;
        }
        if (this.getValue('group_select') || (this.getValue('group_values') && this.getValue('group_label'))) {
            this.selected = options;
            return;
        }
        this.selected = options[this.getValue('value_field')];
    }

    /**
     * Clear selected
     */
    clearSelected() {
        this.selected = this.getBoolean('multiple') ? [] : null;
        this.selectedSet = this.getBoolean('multiple') ? [] : null;
    }

    /**
     * Search with string
     *
     * @param {string} q
     */
    startSearch(q: string): void {
        clearTimeout(this.delayTimer);
        this.searched = false;
        if (!this.hasEndpoint || !q) {
            return;
        }
        this.delayTimer = setTimeout(() => {
            this.doSearch(q);
        }, 1000);
    }

    /**
     * Run the search
     *
     * @param {string} q
     */
    doSearch(q: string): void {
        const api: {[k: string]: any} = {
            name   : this.getObject('endpoint', 'name'),
            method : this.getObject('endpoint', 'method') || 'get',
            params : this.getObject('endpoint', 'params'),
            version: this.getObject('endpoint', 'version') || 1,
        };

        if (!api.params || (typeof api.params === 'object' && Array.isArray(api.params))) {
            api.params = {};
        }

        api.params[this.getValue('search')] = q;
        api.params.including = [this.getValue('label_field'), this.getValue('value_field')];
        this.isSearching = true;
        // TODO: Add services node
        // Resource[api.method](api.name, [api.params, api.version]).then((response) => {
        //     this.searched = true;
        //     const { results } = response.data;
        //     // DEPRECATED: Remove $set in vue 3
        //     this.$set(this, 'parsedOptions', results);
        // }).catch((error) => {
        //     // TODO: Add handleError method from studio
        //     this.handleError(error);
        // }).finally(() => {
        //     this.isSearching = false;
        // });
    }
}

export default YSelect;
